import Vue from 'vue'
import merge from 'lodash/merge'
import applogger from '@applogger'
const jsonwebtoken = () => import('jsonwebtoken')

const inBrowser = () => typeof window !== 'undefined'

const decodeJWT = function(jwt) {
  return jsonwebtoken()
    .then(lib => {
      return lib.decode(jwt)
    })
}

const verifyJWT = function(auth) {
  return jsonwebtoken()
    .then(lib => {
      if (lib.verify(auth.jwt, auth.pubkey)) {
        return lib.decode(auth.jwt)
      }
      throw Error('Authorization Token Verification failed.')
    })
    .then(JWTPayload => {
      if (JWTPayload.expires && new Date() <= new Date(JWTPayload.expires)) {
        return JWTPayload
      }
      throw new Error('Authorization Token Expired')
    })
}

// local storage safe wrappers
function setLocalStorage(key, value) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value)
    }
  }
  catch (err) {
    // No-op
  }
}
function getLocalStorage(key) {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key)
    }
  }
  catch (err) {
    // No-op
  }
  return null
}
function removeLocalStorage(key) {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key)
    }
  }
  catch (err) {
    // No-op
  }
}

const anonymous = {
  user_id: 0,
  roles: [1],
  userRoles: ["anonymoususer"],
  permissions: [],
  name: 'Anonymous'
}

const state = () => ({
  authJWT: '',
  RSAPublicKey: '',
  JWTPayload: {},
  userRoles: [],
  userRolesByID: null,
  Person: false,
  anonPermissions: null,
  userPermissions: null,
  initUserPromise: null,
  refreshInterval: null
})

const actions = {
  init({ commit, state, dispatch, getters }) {
    if (typeof window !== 'undefined') {
      if (!state.initUserPromise || state.initUserPromise.constructor !== Promise) {
        state.initUserPromise = new Promise((resolve, reject) => {
          // Load the JWT and Public Key for authentication from localStorage if available
          let authToken = { jwt: getLocalStorage('JWTToken') }

          // If there is a JWT in location storage - verify it
          if (authToken.jwt) {
            return dispatch('queryPublicKey')
              .then((resp) => {
                authToken.pubkey = resp.data.pubkey
                return authToken
              })
              .then(_ => verifyJWT(authToken)) // If the JWT is valid then store it and attempt to load the user permissions
              .then(payload => {
                commit('storeJWT', { ...authToken, JWTPayload: payload })
                dispatch('checkLoginFreshness', { jwt: authToken.jwt })
                resolve(authToken.jwt)
              })
              .catch(err => {
                reject(err)
              })
          }
          else {
            removeLocalStorage('JWTToken')
            resolve(false)
          }
        })
          .catch(err => {
            applogger.error('Autologin failed: ', err.message)
            removeLocalStorage('JWTToken')
            return false
          })
          .then(jwt => {
            let userPermissions = null
            if (jwt) {
              userPermissions = Promise.all([
                dispatch('getUserPermissions'),
                // This will load the Anonymous Bulk Lookup for Common Items to get the app going and then
                // will replace it with the user specific permissions so that local tasks are correctly updated.
                this.dispatch('node/loadCommonItemsLookup')
                  .then(_ => {
                    this.dispatch('node/loadCommonItemsLookup', { account: state.JWTPayload.user_id })
                  })
              ])
                .then(([userPerms, _lookupItems]) => {
                  return userPerms
                })
            }
            let anonPermissions = dispatch('getAnonymousPermissions')
            return Promise.all([userPermissions, anonPermissions])
          })
          .then(([permissions, anonperms]) => {
            // Store the user permission in the location storage.
            if (anonperms) {
              commit('setAnonPermissions', anonperms)
            }
            if (!permissions && state.userPermissions === null) {
              commit('setUserPermissions', state.anonPermissions)
            }
            else if (state.userPermissions === null) {
              commit('setUserPermissions', permissions)
            }
          })
          .then(_ => this.commit('node/RESET_ITEM_ACCESS_ON_AUTH'))
          .then(_ => true)
      }
      else {
        state.initUserPromise = dispatch('getAnonymousPermissions')
          .then(anonperms => {
            commit('setAnonPermissions', anonperms)
          })
      }
    }
    return state.initUserPromise
  },

  injectAuthJWT({ commit, rootGetters, dispatch }, payload) {
    return rootGetters['getGraphQLClient']().query({
      opName: 'queryPubKey',
      query: `query queryPubKey {
        pubkey: authkey,
      }`
    })
      .then(data => {
        return dispatch('autoLogin', {
          jwt: payload,
          pubkey: data.data.pubkey,
          noerrors: true
        })
      })
  },
  queryPublicKey({ commit, rootGetters }) {
    return rootGetters['getGraphQLClient']().query({
      opName: 'queryPubKey',
      query: `query queryPubKey {
        pubkey: authkey,
      }`
    })
  },
  passwordReset({ commit }, payload) {
    throw new Error('passwordReset user action is deprecated')
    // return POSTRequest('/vuejs/user/password-reset', payload)
  },
  autoLogin({ commit, state, dispatch }, payload) {
    return Promise.all([payload, this.dispatch('generalReset')])
      .then(([auth, _reset]) => {
        return verifyJWT(auth)
          .then(payload => {
            commit('storeJWT', { ...auth, JWTPayload: payload })
            this.dispatch('user/checkLoginFreshness', { jwt: auth.jwt })
            return dispatch('getUserPermissions', auth.jwt)
          })
      })
      .then(permissions => {
        commit('setUserPermissions', permissions)
        return (Object.keys(state.JWTPayload).length > 0)
      })
      .catch(err => {
        if (payload.noerrors !== undefined && payload.noerrors) {
          throw new Error('Autologin failed.')
        }
        applogger.error('Error encounted processing user login request: ', err.message)
        const statusMessage = {
          type: 'error',
          title: 'Error processing the login request',
          text: `
            <p>A problem was encounted processing your login request .</p>
            <p>Please reload the page and try again, if the problem persists
            please contact the site admin.</p>
          <p>Error: ${err.message}</p>
          `,
          notification: false,
          showClose: true
        }
        this.dispatch('reportError', statusMessage)
        return false
      })
  },
  login({ commit, state, rootGetters, dispatch }, payload) {
    return rootGetters['getGraphQLClient']().query({
      query: `query login($u: String!, $p: String!) {
        jwt: login(username: $u, password: $p)
        pubkey: authkey
      }`,
      variables: { u: payload.username, p: payload.password },
      opName: 'login'
    })
      .then(response => {
        if (!response.data.jwt || !response.data.pubkey) {
          throw new Error('Auth failed error')
        }
        return Promise.all([response.data, this.dispatch('generalReset')])
      })
      .then(([auth, _reset]) => {
        return verifyJWT(auth)
          .then(payload => {
            commit('storeJWT', { ...auth, JWTPayload: payload })
            dispatch('checkLoginFreshness', { jwt: auth.jwt })
            return Promise.all([
              dispatch('getUserPermissions', auth.jwt),
              this.dispatch('node/loadCommonItemsLookup', { account: payload.user_id })
            ])
          })
      })
      .then(([permissions, _]) => {
        commit('setUserPermissions', permissions)
        return (Object.keys(state.JWTPayload).length > 0)
      })
      .catch(err => {
        applogger.error('login request - failed to get user permissions: ', err.message)
        if (!payload || !payload.suppressErrorToast) {
          const statusMessage = {
            type: 'error',
            title: 'Error processing the login request',
            text: `
              <p>A problem was encounted processing your login request .</p>
              <p>The system was unable to retreive your account authorizaion.</p>
              <p>Please reload the page and try again, if the problem persists
              please contact the site admin.</p>
            <p>Error: ${err.message}</p>
            `,
            notification: false,
            showClose: true
          }
          this.dispatch('reportError', statusMessage)
        }
        return false
      })
  },
  loadAccountRoles: function({ rootGetters, commit }, payload) {
    if ((payload && !payload.force) && state.userRolesByID) {
      return Promise.resolve(state.userRolesByID)
    }

    const requestPromise = rootGetters['getGraphQLClient']().query({
      query: `query {
        userroles: __type (name:"UserRoles" ) {
          enumValues {
            name
            description
          }
        }
      }`
    })
      .then(response => {
        if (response.data && response.data.userroles.enumValues) {
          const roles = response.data.userroles.enumValues.reduce((acc, { name, description }) => ({ ...acc, [name]: description }), {})
          commit('SET_USERROLES_BY_ID', roles)
          return roles
        }
        return []
      })
    commit('SET_USERROLES_BY_ID', requestPromise)
    return requestPromise
  },

  // TODO: replace calls to this action with the loadAccountRoles Action above as is caches the results
  getUserRolesEnums({ rootGetters }) {
    return rootGetters['getGraphQLClient']().query({
      query: `query {
        whoami {
          roles
        }
      }`
    })
      .then(response => {
        if (response.data && response.data.whoami && response.data.whoami.roles) {
          return response.data.whoami.roles
        }
        return []
      })
  },
  logout({ commit }) {
    commit('removeAuth')
    clearInterval(state.refreshInterval)
    state.refreshInterval = null
    return this.dispatch('generalReset', true)
  },

  getAnonymousPermissions({ state, commit, rootGetters }) {
    // Anonymous (account 0) permissions are the same for every visitor to a
    // given site, not session-specific - safe to fetch during SSR (unlike
    // the rest of init(), which is real per-session JWT/localStorage
    // restoration and genuinely must stay client-only) and to reuse via
    // hydration instead of re-fetching, same self-contained
    // guard-and-commit pattern as node/loadCommonItemsLookup.
    // state.anonPermissions defaults to null and this always resolves an
    // object (at worst {}) on success, so null unambiguously means "not
    // yet loaded" - committing here (rather than leaving it to callers, as
    // before) is what makes this safe to dispatch unconditionally from
    // app.js's SSR/client bootstrap as well as from init()'s existing
    // client-only call site.
    if (state.anonPermissions !== null) {
      return Promise.resolve(state.anonPermissions)
    }

    return this.dispatch('queuedAxiosRequest', { url: '/vuejs/user/permissions/0', config: rootGetters.AuthorizedRequestConfig({ noauth: true }) })
      .then(resp => {
        return resp.data.data.permissions || {}
      })
      .catch(e => {
        applogger.error('Error retreiving User Permissions: ', e.message)
        return {}
      })
      .then(permissions => {
        commit('setAnonPermissions', permissions)
        return permissions
      })
  },

  getUserPermissions({ rootGetters, getters, dispatch, commit }) {
    const uid = getters['userid']
    const p1 = this.dispatch('queuedAxiosRequest', { url: `/vuejs/user/permissions/${uid}`, config: rootGetters.AuthorizedRequestConfig() })
      .then(resp => {
        return resp.data.data.permissions || {}
      })
      .catch(e => {
        applogger.error('Error retreiving User Permissions: ', e.message)
        return {}
      })

    return Promise.all([p1, dispatch('getUserRolesEnums')])
      .then(([permissions, roles]) => {
        commit('setUserRoles', roles)
        return permissions
      })
  },

  loadPerson({ commit, state }, payload) {
    throw new Error('loadPerson Deprecated -')
    // if (Object.keys(state.JWTPayload).length > 0) {
    //   let reset = !!(payload && payload.reset)
    //   if (state.Person === false || reset) {
    //     return GETRequest('/vuejs/user/person')
    //       .then(resp => {
    //         if (resp.node) {
    //           commit('SAVE_ACCOUNT_PERSON', resp.node)
    //         }
    //         else {
    //           commit('SAVE_ACCOUNT_PERSON', {})
    //         }
    //       })
    //   }
    //   return Promise.resolve(state.Person)
    // }
    // return Promise.resolve(false)
  },
  async refreshLogin({ commit, state, rootGetters, dispatch }, payload) {
    return rootGetters['getGraphQLClient']().query({
      query: `query {
        jwt: refreshlogin
        pubkey: authkey
      }`
    })
      .then(response => {
        if (!response.data.jwt || !response.data.pubkey) {
          throw new Error('Auth failed error')
        }
        return Promise.all([response.data, this.dispatch('generalReset')])
      })
      .then(([auth, reset]) => {
        return verifyJWT(auth)
          .then(payload => {
            commit('storeJWT', { ...auth, JWTPayload: payload })
            this.dispatch('user/checkLoginFreshness', { jwt: auth.jwt })
            return dispatch('getUserPermissions', auth.jwt)
          })
      })
      .then(permissions => {
        commit('setUserPermissions', permissions)
        return (Object.keys(state.JWTPayload).length > 0)
      })
      .catch(_err => {
        return false
      })
  },
  async checkLoginFreshness({ state }, payload) {
    let payloadObj
    try {
      payloadObj = await decodeJWT(payload.jwt)
    }
    catch (e) {
      payloadObj = null
    }
    if (payloadObj && payloadObj.refresh) {
      // If the current time is after the refresh time, get a new login
      if (new Date(payloadObj.refresh) < new Date()) {
        clearInterval(state.refreshInterval)
        state.refreshInterval = null
        this.dispatch('user/refreshLogin', payload)
      }
      // Set the interval to the difference between the original timestamp and the refresh time, unless that is more than a day
      let interval = (new Date(payloadObj.refresh) - new Date(payloadObj.timestamp))
      if (interval > 86400000 || interval < 0) interval = 86400000
      // Set a timer to re-call this function every time the interval is up if one isn't already set
      if (!state.refreshInterval) {
        state.refreshInterval = setInterval(() => {
          this.dispatch('user/checkLoginFreshness', payload)
        }, interval)
      }
    }
    else {
      clearInterval(state.refreshInterval)
      state.refreshInterval = null
    }
  },

  getPersonReportData({ commit, rootGetters, dispatch }, payload) {
    return rootGetters['getGraphQLClient']().query({
      opName: 'queryAccount',
      query: `query queryAccount($uid: ID!) {
        account (id: $uid) {
          id
          email
          roles
          rewardroles
          somnambulistroles
          person {
            id
            firstname
            lastname
            dateofbirth
            gender
          }
        }
      }`,
      variables: { uid: payload.uid }
    })
      .then(response => {
        if (!response.data.account || !response.data.account.person) {
          return {
            uid: 0,
            firstname: "Anonymous",
            lastname: "",
            dateofbirth: "",
            gender: "",
            email: "",
            roles: ["anonymoususer"],
            reward_roles: [],
            club_roles: []
          }
        }
        return {
          uid: response.data.account.id,
          firstname: response.data.account.person.firstname,
          lastname: response.data.account.person.lastname,
          dateofbirth: response.data.account.person.dateofbirth,
          gender: response.data.account.person.gender,
          email: response.data.account.email,
          roles: response.data.account.roles,
          reward_roles: response.data.account.rewardroles,
          club_roles: response.data.account.somnambulistroles
        }
      })
  },

  storeunVerifiedJWT({ state, commit }, payload) {
    return verifyJWT({ jwt: payload.jwt, pubkey: payload.pubkey })
      .then(JWTPayload => {
        commit('storeJWT', { ...payload, JWTPayload })
      })
  }
}

const getters = {
  account: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? state.JWTPayload : anonymous
  },
  isLoggedIn: (state) => {
    return (Object.keys(state.JWTPayload).length > 0)
  },
  userid: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? state.JWTPayload.user_id : anonymous.user_id
  },
  mail: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? state.JWTPayload.email : ''
  },
  username: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? state.JWTPayload.name : anonymous.name
  },
  fullname: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? state.JWTPayload.name.fullname : anonymous.name
  },
  roles: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? Object.keys(state.JWTPayload.user_roles) : []
  },
  hasAnyRole: (state) => {
    return (roles) => {
      const checkRoleIDs = roles.map(r => {
        try {
          return Number(r)
        }
        catch (_err) {
          return null
        }
      }).filter(x => x)
      if (Object.keys(state.JWTPayload).length > 0) {
        const check1 = (checkRoleIDs.length) ? Object.keys(state.JWTPayload.user_roles)
          .map(r => Number(r))
          .some(x => checkRoleIDs.includes(x)) : false
        const check2 = state.userRoles.some(role => roles.includes(role))

        return check1 || check2
      }
      else {
        const check1 = (checkRoleIDs.length) ? anonymous.roles.some(x => checkRoleIDs.includes(x)) : false
        const check2 = anonymous.userRoles.some(role => roles.includes(role))
        return check1 || check2
      }
    }
  },
  hasAllRoles: (state) => {
    return (roles) => {
      if (!Array.isArray(roles)) {
        roles = [ roles ]
      }
      if (Object.keys(state.JWTPayload).length > 0) {
        return Object.keys(state.JWTPayload.user_roles)
          .map(r => Number(r))
          .every(x => roles.map(r => Number(r)).includes(x))
      }
      return false
    }
  },
  isDeveloper: (state) => {
    if (state.JWTPayload && Object.keys(state.JWTPayload).length > 0 && Object.keys(state.userPermissions).length > 0) {
      return (state.userPermissions.global.indexOf('developer access') !== -1)
    }
    return false
  },
  checkPermissions: (state) => {
    return (permission, permitDeveloper = true) => {
      if (state.userPermissions && Object.keys(state.userPermissions).length > 0) {
        return (
          (state.userPermissions.global && state.userPermissions.global.includes('developer access') && permitDeveloper) ||
          (permission && state.userPermissions.role && state.userPermissions.role.includes(permission)) ||
          (permission && state.userPermissions.global && state.userPermissions.global.includes(permission))
        )
      }
      return false
    }
  },
  JWT: (state) => {
    return (state.authJWT) ? state.authJWT : false
  },
  verifiedJWT: (state) => {
    return (state.authJWT && state.RSAPublicKey && verifyJWT({ jwt: state.authJWT, pubkey: state.RSAPublicKey })) ? state.authJWT : false
  },
  name: (state) => {
    return (Object.keys(state.JWTPayload).length > 0) ? state.JWTPayload.name : null
  },
  lookupUserRoleName: function(state, getters) {
    return (rid) => {
      if (!state.userRolesByID || typeof state.userRolesByID.then === 'function') {
        return null
      }
      return Object.prototype.hasOwnProperty.call(state.userRolesByID, rid) ? state.userRolesByID[rid] : 'unknown'
    }
  },
  userRolesByID: function(state, getters) {
    if (!state.userRolesByID || typeof state.userRolesByID.then === 'function') {
      return {}
    }
    if (!state.userRoles || state.userRoles.length === 0) {
      return {}
    }
    return state.userRoles.reduce((acc, rid) => {
      const roleName = Object.prototype.hasOwnProperty.call(state.userRolesByID, rid) ? state.userRolesByID[rid] : null
      return roleName ? { ...acc, [rid]: roleName } : acc
    }, {})
  }
}

const mutations = {
  SAVE_ACCOUNT_PERSON(state, person) {
    state.Person = person
  },
  SET_USERROLES_BY_ID: function(state, roles) {
    if (typeof roles.then !== 'function') {
      state.userRolesByID = {}
      Object.entries(roles).forEach(([rid, roleName]) => {
        Vue.set(state.userRolesByID, rid, roleName)
      })
    }
    else {
      state.userRolesByID = roles
    }
  },
  storeJWT(state, authToken) {
    if (!Object.prototype.hasOwnProperty.call(authToken, 'jwt')) {
      applogger.error('storeJWT called without a  jwt parameter')
    }

    if (inBrowser()) {
      setLocalStorage('JWTToken', authToken.jwt)
    }

    state.authJWT = authToken.jwt

    if (authToken.pubkey) {
      state.RSAPublicKey = authToken.pubkey
    }

    if (!Object.prototype.hasOwnProperty.call(authToken, 'JWTPayload')) {
      // This is bad, but we call the verifyJWT function here to ensure that we have a valid JWT payload for other functions
      verifyJWT({ jwt: state.authJWT, pubkey: state.RSAPublicKey })
        .then(JWTPayload => {
          state.JWTPayload = JWTPayload
          // if (inBrowser() && window.logrocket !== undefined) {
          //   window.logrocket.login(state.JWTPayload)
          // }
        })
    }
    else {
      state.JWTPayload = authToken.JWTPayload
      // if (inBrowser() && window.logrocket !== undefined) {
      //   window.logrocket.login(state.JWTPayload)
      // }
    }
  },
  setUserRoles(state, userRoles) {
    state.userRoles = userRoles
  },
  setAnonPermissions(state, permissions) {
    permissions = { global: [], ...permissions }
    // setLocalStorage('AnonPermissions', JSON.stringify(permissions))
    state.anonPermissions = permissions
  },
  setUserPermissions(state, permissions) {
    permissions = merge({}, permissions, { global: state.JWTPayload.permissions || [], role: [], create: [] })
    // setLocalStorage('UserPermissions', JSON.stringify(permissions))
    state.userPermissions = permissions
  },
  removeAuth(state) {
    removeLocalStorage('JWTToken')
    removeLocalStorage('UserPermissions')
    state.authJWT = ''
    state.RSAPublicKey = ''
    state.JWTPayload = {}
    state.userPermissions = state.anonPermissions
    state.userRoles = []

    // if (inBrowser() && window.logrocket !== undefined) {
    //   window.logrocket.logout()
    // }
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
